import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Plus, Bank, CheckCircle, XCircle, ArrowUpRight, CurrencyDollar, FileDoc, X } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { generateCmcDocument } from '../../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { listCmcPayments, createCmcPayment, submitPayment, approvePayment, rejectPayment, markPaymentPaid,
  listCooperators, PAYMENT_QUALIFICATIONS, PAYMENT_STATUSES,
} from '../../services/cmcApi';
import { getCustomTags, createCustomTag } from '../../services/api';
import { CmcPageHeader, CmcStatusBadge, CmcEmpty, fmtAmount, fmtDate } from '../../components/cmc/CmcCommon';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { useBatchSelection } from '../../hooks/useBatchSelection';
import { batchMarkPaidPayments } from '../../services/api';

const emptyForm = {
  cooperator_id: '',
  qualifications: ['SOUTIEN_SOCIAL'],
  gross_amount: 0,
  currency: 'EUR',
  motivation: '',
};

const emptyPaidForm = {
  payment_method: 'virement',
  bank_reference: '',
};

const emptyRejectForm = {
  entity_type: 'payment',
  entity_id: '',
  decision: 'rejected',
  reason: '',
  comments: '',
};

export default function CmcPaymentsPage() {
  const [items, setItems] = useState([]);
  const [cooperators, setCooperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paidDialog, setPaidDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [paidForm, setPaidForm] = useState(emptyPaidForm);
  const batch = useBatchSelection();
  const [batchBusy, setBatchBusy] = useState(false);

  const doBatchMarkPaid = async () => {
    const ids = batch.filterBy(items, (p) => p.status !== 'paid');
    if (!ids.length) { toast.error("Aucun paiement non payé sélectionné"); return; }
    setBatchBusy(true);
    try {
      const r = await batchMarkPaidPayments(ids);
      toast.success(`${r.data.paid.length} paiement(s) marqué(s) payé(s), ${r.data.skipped.length} ignoré(s)`);
      batch.clear();
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur batch'); }
    setBatchBusy(false);
  };

  const exportSelectedCsv = () => {
    const rows = items.filter((p) => batch.isSelected(p.id));
    if (!rows.length) return;
    const csv = "\ufeffRéférence;Coopérateur;Qualification;Montant brut;À payer;Statut;Créé\n" +
      rows.map((p) => [
        p.payment_reference,
        coopName(p.cooperator_id),
        p.qualification,
        p.amounts?.gross_amount || 0,
        p.amounts?.global_amount_to_pay || 0,
        p.status,
        (p.created_at || '').slice(0, 10),
      ].join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `paiements-cmc-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const [rejectForm, setRejectForm] = useState(emptyRejectForm);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const [p, c] = await Promise.all([listCmcPayments({ ...params, limit: 200 }), listCooperators({ limit: 200 })]);
      setItems(p.data.items || []);
      setCooperators(c.data.items || []);
    } catch {
      toast.error('Erreur de chargement des paiements');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [customQualifs, setCustomQualifs] = useState([]);
  const [newQualifLabel, setNewQualifLabel] = useState('');
  const [creatingQualif, setCreatingQualif] = useState(false);

  useEffect(() => {
    getCustomTags('qualification_paiement').then(r => setCustomQualifs(r.data.items || [])).catch(() => {});
  }, []);

  const allQualifs = [
    ...PAYMENT_QUALIFICATIONS,
    ...customQualifs.map(t => ({ value: t.value.toUpperCase(), label: t.label, custom: true })),
  ];
  const findQualif = (v) => allQualifs.find(q => q.value === v);

  const toggleQualif = (val) => {
    const cur = form.qualifications || [];
    setForm({ ...form, qualifications: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] });
  };

  const createQualif = async () => {
    const label = newQualifLabel.trim();
    if (!label) return;
    setCreatingQualif(true);
    try {
      const res = await createCustomTag('qualification_paiement', label);
      const upper = res.data.value.toUpperCase();
      setCustomQualifs([...customQualifs.filter(t => t.value !== res.data.value), res.data]);
      setForm({ ...form, qualifications: [...(form.qualifications || []), upper] });
      setNewQualifLabel('');
      toast.success(`Qualification « ${label} » créée`);
    } catch (e) {
      toast.error("Erreur création qualification");
    } finally {
      setCreatingQualif(false);
    }
  };

  const handleGenerateDoc = async (docType, payment) => {
    try {
      const res = await generateCmcDocument(docType, {
        payment_id: payment.id,
        cooperator_id: payment.cooperator_id,
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docType}_${payment.payment_reference}.docx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Document généré');
    } catch (e) {
      toast.error("Erreur génération document");
    }
  };

  const submit = async () => {
    if (!form.cooperator_id) { toast.error('Sélectionnez un coopérateur'); return; }
    if (!form.qualifications || form.qualifications.length === 0) { toast.error('Sélectionnez au moins une qualification'); return; }
    if (!form.gross_amount || parseFloat(form.gross_amount) <= 0) { toast.error('Montant invalide'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        qualifications: form.qualifications,
        qualification: form.qualifications[0],  // compat
        gross_amount: parseFloat(form.gross_amount),
      };
      await createCmcPayment(payload);
      toast.success('Paiement créé');
      setDialogOpen(false);
      setForm(emptyForm);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Erreur');
    } finally { setSaving(false); }
  };

  const onSubmit = async (p) => {
    try { await submitPayment(p.id); toast.success('Paiement soumis'); fetchData(); }
    catch { toast.error('Erreur'); }
  };
  const onApprove = async (p) => {
    try { await approvePayment(p.id, { entity_type: 'payment', entity_id: p.id, decision: 'approved', validation_body: 'COMITE_CMC' }); toast.success('Paiement approuvé'); fetchData(); }
    catch { toast.error('Erreur'); }
  };
  const doMarkPaid = async () => {
    setSaving(true);
    try { await markPaymentPaid(paidDialog.id, paidForm); toast.success('Paiement marqué comme payé'); setPaidDialog(null); setPaidForm(emptyPaidForm); fetchData(); }
    catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };
  const doReject = async () => {
    if (!rejectForm.reason) { toast.error('Motif requis'); return; }
    setSaving(true);
    try {
      await rejectPayment(rejectDialog.id, { ...rejectForm, entity_id: rejectDialog.id });
      toast.success('Paiement rejeté');
      setRejectDialog(null);
      setRejectForm(emptyRejectForm);
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const coopMap = Object.fromEntries(cooperators.map((c) => [c.id, c]));
  const coopName = (id) => {
    const c = coopMap[id];
    if (!c) return id;
    return c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim();
  };

  return (
    <div data-testid="cmc-payments-page">
      <CmcPageHeader
        title="Paiements coopératifs"
        subtitle="Versements aux coopérateurs (cycle de validation CMC)"
        actions={
          <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} className="bg-white text-[#D5007D] hover:bg-white/90 font-bold" data-testid="payment-create-btn">
            <Plus size={16} className="mr-2" /> Nouveau paiement
          </Button>
        }
      />

      {batch.hasSelection && (
        <Card className="border-[#2D5A4C] bg-[#F0F8E4] mb-4" data-testid="payment-batch-toolbar">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <p className="text-sm font-bold text-[#2D5A4C]">{batch.count} sélectionné(s)</p>
            <div className="flex-1"></div>
            <Button
              size="sm"
              onClick={doBatchMarkPaid}
              disabled={batchBusy}
              className="bg-[#2D5A4C] hover:bg-[#1E4136]"
              data-testid="payment-batch-mark-paid"
            >
              {batchBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle size={12} className="mr-1" />}
              Marquer payé ({batch.filterBy(items, (p) => p.status !== 'paid').length})
            </Button>
            <Button
              size="sm"
              onClick={exportSelectedCsv}
              variant="outline"
              className="border-[#2D5A4C] text-[#2D5A4C]"
              data-testid="payment-batch-export"
            >
              📥 Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={batch.clear} data-testid="payment-batch-clear">Effacer</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E8E6E1] mb-4">
        <CardContent className="p-4 flex items-center gap-3">
          <Label className="text-xs text-[#5E6D66]">Filtre statut :</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[220px]" data-testid="payment-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {PAYMENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : items.length === 0 ? (
            <CmcEmpty icon={Bank} message="Aucun paiement enregistré" hint="Créez le premier paiement coopératif" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium w-8">
                      <Checkbox
                        checked={items.length > 0 && items.every((p) => batch.isSelected(p.id))}
                        onCheckedChange={(v) => (v ? batch.selectAll(items) : batch.clear())}
                        data-testid="payment-batch-select-all"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">Référence</th>
                    <th className="text-left px-4 py-2.5 font-medium">Coopérateur</th>
                    <th className="text-left px-4 py-2.5 font-medium">Qualification</th>
                    <th className="text-right px-4 py-2.5 font-medium">Montant brut</th>
                    <th className="text-right px-4 py-2.5 font-medium">À payer</th>
                    <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                    <th className="text-left px-4 py-2.5 font-medium">Créé</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {items.map((p) => (
                    <tr key={p.id} className={`hover:bg-[#FAFAF8] ${batch.isSelected(p.id) ? 'bg-[#F0F8E4]' : p.enhanced_review_required ? 'bg-amber-50/40' : ''}`} data-testid={`payment-row-${p.id}`}>
                      <td className="px-3 py-3">
                        <Checkbox
                          checked={batch.isSelected(p.id)}
                          onCheckedChange={() => batch.toggle(p.id)}
                          data-testid={`payment-select-${p.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{p.payment_reference}</td>
                      <td className="px-4 py-3 text-[#1A2421]">{coopName(p.cooperator_id)}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">
                        {(PAYMENT_QUALIFICATIONS.find((q) => q.value === p.qualification)?.label) || p.qualification}
                        {p.enhanced_review_required && (
                          <div className="text-[10px] mt-0.5 text-amber-700 font-medium">⚠ Revue renforcée</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-[#1A2421]">{fmtAmount(p.gross_amount, p.currency)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#1A2421]">{fmtAmount(p.amounts?.global_amount_to_pay, p.currency)}</td>
                      <td className="px-4 py-3"><CmcStatusBadge status={p.status} label={PAYMENT_STATUSES.find((s) => s.value === p.status)?.label} /></td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{fmtDate(p.created_at)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {p.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => onSubmit(p)} data-testid={`payment-submit-${p.id}`}>
                            <ArrowUpRight size={14} className="mr-1" /> Soumettre
                          </Button>
                        )}
                        {(p.status === 'submitted' || p.status === 'under_review') && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => onApprove(p)} className="text-emerald-700 border-emerald-200 mr-1" data-testid={`payment-approve-${p.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Approuver
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setRejectForm({ ...emptyRejectForm, entity_id: p.id }); setRejectDialog(p); }} className="text-red-700 border-red-200" data-testid={`payment-reject-${p.id}`}>
                              <XCircle size={14} className="mr-1" /> Rejeter
                            </Button>
                          </>
                        )}
                        {p.status === 'approved' && (
                          <Button size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={() => { setPaidForm(emptyPaidForm); setPaidDialog(p); }} data-testid={`payment-mark-paid-${p.id}`}>
                            <CurrencyDollar size={14} className="mr-1" /> Marquer payé
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="ml-1" data-testid={`payment-generate-doc-${p.id}`}>
                              <FileDoc size={14} className="mr-1" /> Générer
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {[
                              { key: 'attestation_nominative', label: 'Attestation nominative' },
                              { key: 'accuse_mission', label: 'Accusé mission' },
                              { key: 'convention_cipaesse', label: 'Convention Cadre C.I.P.A.E.S.S.E' },
                              { key: 'convention_cadre', label: 'Convention Cadre C.I.P.A.S.C.O.P' },
                              { key: 'convention_apport_industrie', label: 'Convention Apport en industrie' },
                              { key: 'ordre_mission', label: 'Ordre de mission' },
                              { key: 'pv_reception', label: 'PV de réception' },
                            ].map(t => (
                              <DropdownMenuItem key={t.key} onSelect={() => handleGenerateDoc(t.key, p)} data-testid={`gen-doc-${t.key}-${p.id}`}>
                                {t.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau paiement coopératif</DialogTitle>
            <DialogDescription>Initiez un versement; il devra ensuite être soumis et validé.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Coopérateur *</Label>
              <Select value={form.cooperator_id} onValueChange={(v) => setForm({ ...form, cooperator_id: v })}>
                <SelectTrigger data-testid="payment-form-cooperator"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {cooperators.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim()} ({c.numero_associe})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Qualifications *</Label>
              <div className="border border-[#E8E6E1] rounded-md p-2 max-h-40 overflow-y-auto" data-testid="payment-form-qualification-multi">
                {allQualifs.map(q => {
                  const checked = (form.qualifications || []).includes(q.value);
                  return (
                    <label key={q.value} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-[#F9F8F5] rounded px-1">
                      <input type="checkbox" checked={checked} onChange={() => toggleQualif(q.value)} data-testid={`qualif-check-${q.value}`} />
                      <span>{q.label}</span>
                      {q.custom && <Badge variant="outline" className="text-[10px]">personnalisée</Badge>}
                    </label>
                  );
                })}
              </div>
              {(form.qualifications || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.qualifications.map(v => (
                    <Badge key={v} variant="outline" className="text-xs gap-1">
                      {findQualif(v)?.label || v}
                      <button type="button" onClick={() => toggleQualif(v)} className="ml-1 hover:text-red-600"><X size={11} /></button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Input
                  placeholder="Créer une nouvelle qualification…"
                  value={newQualifLabel}
                  onChange={(e) => setNewQualifLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createQualif(); } }}
                  className="text-sm"
                  data-testid="qualif-new-input"
                />
                <Button type="button" size="sm" variant="outline" onClick={createQualif} disabled={!newQualifLabel.trim() || creatingQualif} data-testid="qualif-new-create">
                  {creatingQualif ? <Loader2 className="animate-spin" size={14} /> : '+ Créer'}
                </Button>
              </div>
            </div>
            <div>
              <Label>Montant brut *</Label>
              <Input type="number" step="0.01" data-testid="payment-form-amount" value={form.gross_amount} onChange={(e) => setForm({ ...form, gross_amount: e.target.value })} />
            </div>
            <div>
              <Label>Devise</Label>
              <Input data-testid="payment-form-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Motivation</Label>
              <Input data-testid="payment-form-motivation" value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} placeholder="Justification du versement" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="payment-cancel-btn">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="payment-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paidDialog} onOpenChange={(o) => !o && setPaidDialog(null)}>
        <DialogContent className="max-w-md" data-testid="payment-paid-dialog">
          <DialogHeader>
            <DialogTitle>Marquer comme payé</DialogTitle>
            <DialogDescription>{paidDialog?.payment_reference}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mode de paiement</Label>
              <Select value={paidForm.payment_method} onValueChange={(v) => setPaidForm({ ...paidForm, payment_method: v })}>
                <SelectTrigger data-testid="payment-paid-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Référence bancaire</Label>
              <Input data-testid="payment-paid-ref" value={paidForm.bank_reference} onChange={(e) => setPaidForm({ ...paidForm, bank_reference: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDialog(null)} data-testid="payment-paid-cancel">Annuler</Button>
            <Button onClick={doMarkPaid} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="payment-paid-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectDialog} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent className="max-w-md" data-testid="payment-reject-dialog">
          <DialogHeader>
            <DialogTitle>Rejeter le paiement</DialogTitle>
            <DialogDescription>{rejectDialog?.payment_reference}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Motif *</Label>
              <Input data-testid="payment-reject-reason" value={rejectForm.reason} onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })} />
            </div>
            <div>
              <Label>Commentaires</Label>
              <Input data-testid="payment-reject-comments" value={rejectForm.comments} onChange={(e) => setRejectForm({ ...rejectForm, comments: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)} data-testid="payment-reject-cancel">Annuler</Button>
            <Button onClick={doReject} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="payment-reject-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rejeter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
